import React, { useState, useCallback } from "react";
import { Formik, FieldArray } from "formik";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import AdMaxHeader from "./header";
import { updateUserProfile, updateVideoAds } from "../../../store/slices/authSlice";
import VideoModel from "../../../components/models/VideoModel";
import { toast } from "react-toastify";
import AdsUnpaid from "./AdsUnpaid";
import Swal from 'sweetalert2';

// Create a validation function that accepts deletedVideos
const createValidationSchema = (deletedVideos) => {
    return Yup.object({
        videos: Yup.array().of(
            Yup.object().shape({
                video: Yup.string(),
                link: Yup.string().url("Enter a valid URL"),
                videoIdentifier: Yup.string()
                    .test('video-identifier', 'Video Identifier is required', function (value) {
                        // Get the current index from the path
                        const path = this.path;
                        const match = path.match(/videos\[(\d+)\]\.videoIdentifier/);
                        if (match) {
                            const index = parseInt(match[1]);
                            // Skip validation if video is deleted
                            if (deletedVideos.includes(index)) {
                                return true;
                            }
                        }
                        // Validate only if video is not deleted
                        if (!value || value.trim() === '') {
                            return this.createError({ message: 'Video Identifier is required' });
                        }
                        if (value.length < 5) {
                            return this.createError({ message: 'Video Identifier must be at least 5 characters' });
                        }
                        if (value.length > 32) {
                            return this.createError({ message: 'Video Identifier must be at most 32 characters' });
                        }
                        return true;
                    })
            })
        ),
    });
};

const VideoAd = () => {
    const { user } = useSelector((state) => state.auth);
    const { attributes: { videoAds, enableVideoAds } } = user;
    const dispatch = useDispatch();
    const [deletedVideos, setDeletedVideos] = useState([]);
    const [videoFile, setVideoFile] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const handleDelete = (index) => {
        setDeletedVideos([...deletedVideos, index]);
    };

    const handleUndoDelete = (index) => {
        setDeletedVideos(deletedVideos.filter((i) => i !== index));
    };

    const showAlert = () => {
        Swal.fire("Not-Allowed", "Video Ads is a premium feature that can be used in your application.<br> Please contact us for pricing: support@radiofxinc.com.");
    }

    const validateFile = useCallback((file) => {
        const maxSize = 10 * 1024 * 1024; // 10MB
        const allowedTypes = ["video/mp4", "video/quicktime"];

        if (!allowedTypes.includes(file.type)) {
            toast.error("Invalid file type. Please upload an MP4 or MOV file.");
            return false;
        }
        if (file.size > maxSize) {
            toast.error("File size exceeds 10MB. Please upload a smaller file.");
            return false;
        }
        return true;
    }, []);

    const handleFileSelect = useCallback((file) => {
        if (file && validateFile(file)) {
            setVideoFile(file);
            setShowModal(true);
        }
    }, [validateFile]);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        setIsDragging(false);
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            handleFileSelect(files[0]);
        }
    }, [handleFileSelect]);

    // Pre-process existing videos to ensure they have videoIdentifier
    const preprocessVideos = (videos) => {
        return videos?.map(video => ({
            ...video,
            videoIdentifier: video.videoIdentifier || "" // Ensure empty string if missing
        })) || [];
    };

    // Create validation schema with current deletedVideos
    const validationSchema = createValidationSchema(deletedVideos);

    return (
        <Formik
            initialValues={{
                videos: preprocessVideos(videoAds?.videos),
            }}
            validationSchema={validationSchema}
            validateOnChange={true}
            validateOnBlur={true}
            enableReinitialize={true}
            onSubmit={async (values, { setSubmitting }) => {
                try {
                    // Filter out deleted videos before submission
                    const isForStation = user?.attributes?.isOwner == false ? true : false;
                    const filteredVideos = values.videos.filter((_, index) => !deletedVideos.includes(index));
                    const updatedValues = {
                        ...values,
                        videos: filteredVideos,
                    };

                    await dispatch(updateVideoAds({ values: { videoAds: updatedValues }, message: 'Video Ad updated successfully', isForStation }));
                    setDeletedVideos([]);
                } catch (error) {
                    console.error("Submission error:", error.message);
                    toast.error("Failed to update Video Ad");
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            {(formik) => {
                // Define handleVideoUploadSuccess inside the Formik render function
                const handleVideoUploadSuccess = (videoUrl, videoIdentifier) => {
                    // Add the new video to the form values
                    formik.setFieldValue('videos', [
                        ...formik.values.videos,
                        {
                            video: videoUrl,
                            videoIdentifier: videoIdentifier,
                            link: ""
                        }
                    ]);
                    setVideoFile(null);
                    setShowModal(false);
                };

                // Function to manually validate a specific field
                const validateField = (fieldName) => {
                    formik.setFieldTouched(fieldName, true, true);
                };

                return (
                    <div className="section_main_div">
                        <h1 className="section_heading">AdMax</h1>
                        <AdMaxHeader />
                        {!enableVideoAds && <AdsUnpaid title={'Video Ads'} />}
                        <div className="InnerHeading">
                            <h4  >Add Video</h4>
                            <h5 className="pb-2">Size: 10 MB<br />Types: mp4, mov<br />Video Resolution : (480x320, 320x480, 300x250, 300x600)</h5>
                        </div>
                        <form className="form_div" onSubmit={formik.handleSubmit}>
                            <FieldArray name="videos">
                                {({ remove, push }) => (
                                    <>
                                        {formik.values.videos?.map((video, index) => (
                                            <div className="form-card" key={index} style={{ position: "relative" }}>
                                                <div className="form-elements" style={{ position: "relative", zIndex: 2 }}>
                                                    <div className="d-flex">
                                                        <video src={video.video} controls style={{ width: '100%', maxWidth: '400px' }} />
                                                        {!deletedVideos.includes(index) && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleDelete(index)}
                                                                style={{ border: "0px", outline: "0px", cursor: "pointer", background: "none", marginLeft: "10px" }}
                                                            >
                                                                Delete
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div className="custom-form-group">
                                                        <label htmlFor={`videos.${index}.videoIdentifier`}>Video Identifier</label>
                                                        <input
                                                            type="text"
                                                            id={`videos.${index}.videoIdentifier`}
                                                            {...formik.getFieldProps(`videos.${index}.videoIdentifier`)}
                                                            autoComplete="off"
                                                            onBlur={(e) => {
                                                                formik.handleBlur(e);
                                                                validateField(`videos.${index}.videoIdentifier`);
                                                            }}
                                                            required
                                                            // Disable input if video is deleted
                                                            disabled={deletedVideos.includes(index)}
                                                        />
                                                        <input
                                                            type="hidden"
                                                            id={`videos.${index}.video`}
                                                            {...formik.getFieldProps(`videos.${index}.video`)}
                                                            autoComplete="off"
                                                        />
                                                        {/* Only show error if video is NOT deleted */}
                                                        {!deletedVideos.includes(index) && formik.touched.videos?.[index]?.videoIdentifier && formik.errors.videos?.[index]?.videoIdentifier ? (
                                                            <div className="error-text">{formik.errors.videos[index].videoIdentifier}</div>
                                                        ) : null}
                                                        <hr className={!deletedVideos.includes(index) && formik.touched.videos?.[index]?.videoIdentifier && formik.errors.videos?.[index]?.videoIdentifier ? "error-hr-line" : ""} />
                                                    </div>
                                                    <div className="custom-form-group">
                                                        <label htmlFor={`videos.${index}.link`}>Add Link</label>
                                                        <input
                                                            type="text"
                                                            id={`videos.${index}.link`}
                                                            {...formik.getFieldProps(`videos.${index}.link`)}
                                                            autoComplete="off"
                                                            onBlur={(e) => {
                                                                formik.handleBlur(e);
                                                                validateField(`videos.${index}.link`);
                                                            }}
                                                            // Disable input if video is deleted
                                                            disabled={deletedVideos.includes(index)}
                                                        />
                                                        {formik.touched.videos?.[index]?.link && formik.errors.videos?.[index]?.link ? (
                                                            <div className="error-text">{formik.errors.videos[index].link}</div>
                                                        ) : null}
                                                        <hr className={formik.touched.videos?.[index]?.link && formik.errors.videos?.[index]?.link ? "error-hr-line" : ""} />

                                                    </div>

                                                </div>

                                                {deletedVideos.includes(index) && (
                                                    <div
                                                        style={{
                                                            position: "absolute",
                                                            top: 0,
                                                            left: 0,
                                                            width: "100%",
                                                            height: "100%",
                                                            background: "rgba(0, 0, 0, 0.65)",
                                                            display: "flex",
                                                            justifyContent: "center",
                                                            alignItems: "center",
                                                            zIndex: 5,
                                                        }}
                                                    >
                                                        <button
                                                            type="button"
                                                            onClick={() => handleUndoDelete(index)}
                                                            className="simpleBtn"
                                                        >
                                                            <span>UNDO DELETE</span>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ))}

                                        <div className="form-card">
                                            <div
                                                className="file-upload"
                                                style={{
                                                    height: "100%",
                                                    border: isDragging ? "2px dashed rgb(74, 143, 226)" : "1px solid rgb(151, 151, 151)",
                                                    padding: "18px 40px",
                                                    cursor: "pointer",
                                                    fontWeight: 500,
                                                    textAlign: "center",
                                                    color: "rgb(74, 143, 226)",
                                                    backgroundColor: isDragging ? "rgba(74, 143, 226, 0.1)" : "transparent",
                                                    transition: "all 0.3s ease"
                                                }}
                                                onClick={() => { !enableVideoAds ? showAlert() : document.getElementById("fileInput").click(); }}
                                                onDragOver={!enableVideoAds ? showAlert : handleDragOver}
                                                onDragLeave={!enableVideoAds ? showAlert : handleDragLeave}
                                                onDrop={!enableVideoAds ? showAlert : handleDrop}
                                            >
                                                <input
                                                    type="file"
                                                    id="fileInput"
                                                    accept="video/mp4,video/quicktime,.mp4,.mov"
                                                    style={{ display: "none" }}
                                                    onChange={(e) => {
                                                        const file = e.target.files[0];
                                                        if (file) {
                                                            handleFileSelect(file);
                                                            // Reset the file input
                                                            e.target.value = null;
                                                        }
                                                    }}
                                                />
                                                {isDragging ? "Drop video here" : "+ Add Video file. Drop file"}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </FieldArray>

                            <div className="text-right">
                                <div className="button-div">
                                    <button
                                        type="button"
                                        onClick={formik.submitForm}
                                        className="text-white"
                                        disabled={formik.isSubmitting || !enableVideoAds}
                                    >
                                        {formik.isSubmitting ? "Saving..." : "Save"}
                                    </button>
                                </div>
                            </div>
                        </form>

                        <VideoModel
                            show={showModal}
                            onClose={() => {
                                setShowModal(false);
                                setVideoFile(null);
                            }}
                            video={videoFile}
                            onUploadSuccess={handleVideoUploadSuccess}
                        />
                    </div>
                );
            }}
        </Formik>
    );
};

export default VideoAd;